import { useEffect, useState } from 'react';

/**
 * @param {string | number} currentTime
 * @param {string | number} expiryTime
 * @param {boolean} isIncrement - Flag to determine if timer should increment or decrement
 * @returns {Object}
 * duration object with keys
 * days
 * minutes
 * seconds
 * minutes
 * asMilliSeconds
 * asSeconds
 * asMinutes
 * asHours
 * asDays
 */
const getDuration = (currentTime, expiryTime, isIncrement = false) => {
  const currentDateTime = new Date(currentTime);
  const expiryDateTime = new Date(expiryTime);
  const milliseconds = isIncrement
    ? currentDateTime - expiryDateTime
    : expiryDateTime - currentDateTime;
  let seconds = Math.floor(milliseconds / 1000);
  let minutes = Math.floor(seconds / 60);
  let hours = Math.floor(minutes / 60);
  let days = Math.floor(hours / 24);

  const asMilliSeconds = () => {
    return isIncrement
      ? currentDateTime - expiryDateTime
      : expiryDateTime - currentDateTime;
  };
  const asSeconds = () => {
    return asMilliSeconds() / 1000;
  };
  const asMinutes = () => {
    return asSeconds() / 60;
  };
  const asHours = () => {
    return asMinutes() / 60;
  };
  const asDays = () => {
    return asHours() / 24;
  };

  seconds %= 60;
  minutes %= 60;
  hours %= 24;

  if (milliseconds < 0) {
    seconds = 0;
    minutes = 0;
    hours = 0;
    days = 0;
  }

  return {
    seconds: seconds < 10 ? `0${seconds}` : seconds,
    minutes: minutes < 10 ? `0${minutes}` : minutes,
    hours: days > 0 || hours < 10 ? `0${hours}`.slice(-2) : hours,
    days,
    asMilliSeconds,
    asSeconds,
    asMinutes,
    asHours,
    asDays,
  };
};

/**
 * Calculate timer on each second and return duration object
 * @param {string} expiryTime - The target time for the timer
 * @param {Function} onExpiry - Callback function when timer reaches target
 * @param {string} currentTime - Current time to start from
 * @param {boolean} isTimerStopped - Flag to stop the timer
 * @param {Function} onExpiryTimeChange - Callback when expiry time changes
 * @param {boolean} isIncrement - Flag to determine if timer should increment or decrement
 * @throws {Error} if expiry time is not provided or callbacks are not functions
 * @returns {Object} Duration object with time units and conversion methods
 */
const useTimer = (
  expiryTime,
  onExpiry,
  currentTime = new Date(),
  isTimerStopped = false,
  onExpiryTimeChange,
  isIncrement = false
) => {
  if (!expiryTime) {
    throw new Error('expiryTime is required');
  }
  if (onExpiry && typeof onExpiry !== 'function') {
    throw new Error('onExpiry should be function');
  }

  if (onExpiryTimeChange && typeof onExpiryTimeChange !== 'function') {
    throw new Error('onExpiryTimeChange should be function');
  }

  const [duration, setDuration] = useState(() => {
    const newDuration = getDuration(currentTime, expiryTime, isIncrement);
    if (!isIncrement && newDuration.asSeconds() <= 0 && onExpiry) {
      onExpiry();
    }
    return newDuration;
  });
  useEffect(() => {
    if (!isTimerStopped) {
      const timer = setTimeout(() => {
        const newDuration = getDuration(currentTime, expiryTime, isIncrement);
        setDuration(newDuration);
        if (!isIncrement && newDuration.asSeconds() <= 0 && onExpiry) {
          onExpiry();
        }
      }, 1000);
      return () => {
        clearTimeout(timer);
      };
    }
    return () => {};
  }, [currentTime, expiryTime, isTimerStopped, isIncrement]);

  useEffect(() => {
    setDuration(getDuration(currentTime, expiryTime, isIncrement));
    if (onExpiryTimeChange && typeof onExpiryTimeChange === 'function') {
      onExpiryTimeChange(false);
    }
  }, [expiryTime, onExpiryTimeChange, isIncrement]);

  return duration;
};

export default useTimer;
